import { useEffect, useState } from 'react'
import { api, ApiError, Aborted } from '../api/client'
import type { Instrument, WatchlistItem } from '../api/client'
import { Dialog } from '../components/common/Dialog'
import { Input } from '../components/common/Controls'
import { Loading } from '../components/common/Feedback'
import {
  WATCHLIST_REASON,
  WATCHLIST_REASON_HINT,
  grundName,
} from './watchlistVocabulary'
import s from './WatchlistDialogs.module.scss'

/**
 * Die Pflege der Watchlist — das einzige Schreiben der Oberfläche.
 *
 * Alles andere trägt der MCP-Zugang ein. Der Unterschied: Was beobachtet
 * wird, ist eine AUSWAHL, und die trifft ein Mensch am Schirm. Einschätzungen
 * sind Urteile — die kommen vom Analysten über das Werkzeug.
 */

const GRUENDE = Object.keys(WATCHLIST_REASON)

/** Die Fehlermeldung des Servers, oder ein Satz, der weiterhilft. */
function fehlertext(cause: unknown): string {
  if (cause instanceof ApiError) return cause.message
  return 'Die Änderung ist nicht angekommen. Verbindung prüfen und erneut versuchen.'
}

/**
 * Die Gründe als Kästchen.
 *
 * Kästchen und keine Freitextliste: Die drei Werte sind festgelegt, und der
 * Dienst weist alles andere mit 400 ab. Wer tippen dürfte, bekäme den Fehler
 * erst nach dem Absenden.
 */
function Gruende({ gewaehlt, onChange }: {
  gewaehlt: string[]
  onChange: (werte: string[]) => void
}) {
  function umschalten(grund: string) {
    onChange(gewaehlt.includes(grund)
      ? gewaehlt.filter((g) => g !== grund)
      : [...gewaehlt, grund])
  }

  return (
    <fieldset className={s.gruende}>
      <legend className={s.legende}>
        Gründe <span className={s.pflicht}>mindestens einer</span>
      </legend>
      {GRUENDE.map((grund) => (
        <label key={grund} className={s.grund}>
          <input type="checkbox" checked={gewaehlt.includes(grund)}
                 onChange={() => umschalten(grund)} />
          <span className={s.grundName}>{grundName(grund)}</span>
          <span className={s.grundHinweis}>{WATCHLIST_REASON_HINT[grund]}</span>
        </label>
      ))}
    </fieldset>
  )
}

/**
 * Der Titel wird GESUCHT, nicht getippt.
 *
 * Bei über sechzigtausend Instrumenten legt eine falsch abgeschriebene ISIN
 * entweder gar nichts an — oder einen Eintrag zum falschen Papier.
 */
function Instrumentsuche({ gewaehlt, onWaehlen }: {
  gewaehlt: Instrument | null
  onWaehlen: (instrument: Instrument | null) => void
}) {
  const [suche, setSuche] = useState('')
  const [treffer, setTreffer] = useState<Instrument[]>([])
  const [laedt, setLaedt] = useState(false)

  useEffect(() => {
    const begriff = suche.trim()
    // Wie in der Stammdatensuche: Kürzer zu suchen liefert fast alles zurück.
    if (gewaehlt || begriff.length < 2) { setTreffer([]); return }

    const abbruch = new AbortController()
    // Erst tippen lassen, dann fragen: Ohne die Pause liefe je Tastendruck
    // eine Abfrage über sechzigtausend Zeilen.
    const timer = window.setTimeout(() => {
      setLaedt(true)
      api.masterData({ q: begriff, size: 8 }, abbruch.signal)
        .then((antwort) => { setTreffer(antwort.items); setLaedt(false) })
        .catch((cause) => { if (!(cause instanceof Aborted)) setLaedt(false) })
    }, 250)

    return () => { window.clearTimeout(timer); abbruch.abort() }
  }, [suche, gewaehlt])

  if (gewaehlt) {
    return (
      <div className={s.gewaehlt}>
        <div>
          <div className={s.gewaehltName}>{gewaehlt.name}</div>
          <div className={s.gewaehltKennung}>
            {gewaehlt.isin} · {gewaehlt.primary_exchange}
          </div>
        </div>
        <button type="button" className={s.wechseln}
                onClick={() => { onWaehlen(null); setSuche('') }}>
          ändern
        </button>
      </div>
    )
  }

  return (
    <div>
      <Input label="Instrument" value={suche} autoFocus
             placeholder="Name, ISIN oder Symbol — mindestens zwei Zeichen"
             onChange={(event) => setSuche(event.target.value)} />
      {laedt && <Loading />}
      {!laedt && treffer.length > 0 && (
        <ul className={s.treffer}>
          {treffer.map((instrument) => (
            <li key={instrument.id}>
              <button type="button" className={s.treffer_zeile}
                      onClick={() => onWaehlen(instrument)}>
                <span className={s.trefferName}>{instrument.name}</span>
                <span className={s.trefferKennung}>
                  {instrument.isin} · {instrument.primary_exchange}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {!laedt && suche.trim().length >= 2 && treffer.length === 0 && (
        <p className={s.nichts}>Kein Titel gefunden.</p>
      )}
    </div>
  )
}

/** Einen Titel aufnehmen. */
export function AufnehmenDialog({ onFertig, onAbbrechen }: {
  onFertig: () => void
  onAbbrechen: () => void
}) {
  const [instrument, setInstrument] = useState<Instrument | null>(null)
  const [gruende, setGruende] = useState<string[]>([])
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  function aufnehmen() {
    if (!instrument) return
    setLaeuft(true)
    setFehler(null)
    api.watchlistAdd(instrument.isin, gruende)
      .then(onFertig)
      .catch((cause) => { setFehler(fehlertext(cause)); setLaeuft(false) })
  }

  return (
    <Dialog titel="Titel aufnehmen" bestaetigen="Aufnehmen"
            gesperrt={!instrument || gruende.length === 0}
            laeuft={laeuft} fehler={fehler}
            onBestaetigen={aufnehmen} onAbbrechen={onAbbrechen}>
      <Instrumentsuche gewaehlt={instrument} onWaehlen={setInstrument} />
      <Gruende gewaehlt={gruende} onChange={setGruende} />
    </Dialog>
  )
}

/** Die Gründe eines Eintrags ändern. */
export function GruendeDialog({ eintrag, onFertig, onAbbrechen }: {
  eintrag: WatchlistItem
  onFertig: () => void
  onAbbrechen: () => void
}) {
  const [gruende, setGruende] = useState<string[]>(eintrag.reasons)
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  function speichern() {
    setLaeuft(true)
    setFehler(null)
    api.watchlistSetReasons(eintrag.id, gruende)
      .then(onFertig)
      .catch((cause) => { setFehler(fehlertext(cause)); setLaeuft(false) })
  }

  return (
    <Dialog titel={`Gründe — ${eintrag.name}`} bestaetigen="Speichern"
            gesperrt={gruende.length === 0} laeuft={laeuft} fehler={fehler}
            onBestaetigen={speichern} onAbbrechen={onAbbrechen}>
      <Gruende gewaehlt={gruende} onChange={setGruende} />
    </Dialog>
  )
}

/**
 * Ruhen lassen oder wieder aufnehmen.
 *
 * Ein Dialog und nicht ein Klick: Der Titel fällt damit aus der Arbeitsliste
 * und aus den Reports, die auf ihr stehen. Zurück geht es, aber erst, wenn
 * jemand es merkt.
 */
export function RuhenDialog({ eintrag, onFertig, onAbbrechen }: {
  eintrag: WatchlistItem
  onFertig: () => void
  onAbbrechen: () => void
}) {
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const ruhenLassen = eintrag.active

  function umschalten() {
    setLaeuft(true)
    setFehler(null)
    api.watchlistSetActive(eintrag.id, !eintrag.active)
      .then(onFertig)
      .catch((cause) => { setFehler(fehlertext(cause)); setLaeuft(false) })
  }

  return (
    <Dialog titel={ruhenLassen ? 'Ruhen lassen' : 'Wieder aufnehmen'}
            bestaetigen={ruhenLassen ? 'Ruhen lassen' : 'Wieder aufnehmen'}
            variant={ruhenLassen ? 'danger' : 'primary'}
            laeuft={laeuft} fehler={fehler}
            onBestaetigen={umschalten} onAbbrechen={onAbbrechen}>
      <p className={s.frage}>
        {ruhenLassen
          ? <><strong>{eintrag.name}</strong> fällt aus der Arbeitsliste und
              aus den Reports, die auf ihr stehen. Der Eintrag bleibt mit
              seinen Gründen erhalten — ruhend heißt nicht abgeschlossen.</>
          : <><strong>{eintrag.name}</strong> kommt zurück in die Arbeitsliste,
              mit den Gründen von damals: {eintrag.reasons.map(grundName)
                .join(', ')}.</>}
      </p>
    </Dialog>
  )
}
